import { Component, OnInit, OnDestroy, ChangeDetectionStrategy, ChangeDetectorRef, ViewEncapsulation, inject } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { lastValueFrom } from 'rxjs';

import { Client } from 'src/providers/client/client';

import { ConciergeApiService } from 'src/shared/services/concierge-api.service';
import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { WindowService } from 'src/shared/providers/window.service';
import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { TidyStorage } from 'src/shared/providers/tidy-storage';
import { Util } from 'src/shared/util/util';

import { AttachmentModel } from 'src/models/concierge.model';

@Component({
  selector: 'guidebook-onboarding',
  templateUrl: './guidebook-onboarding.component.html',
  styleUrls: ['./guidebook-onboarding.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None
})
export class GuidebookOnboardingComponent implements OnInit, OnDestroy {
  private client = inject(Client);
  private rightSidePanelService = inject(RightSidePanelService);
  private navCtrl = inject(CustomNavController);
  private cdr = inject(ChangeDetectorRef);
  private util = inject(Util);
  private storage = inject(TidyStorage);
  private conciergeApi = inject(ConciergeApiService);
  private fb = inject(FormBuilder);
  windowService = inject(WindowService);

  private metadataKey: string;
  private addressId: number;
  private readonly stepId = 'guidebook';

  loaded = false;
  fromGettingStarted = false;
  attachments: AttachmentModel[] = [];
  submitted = false;
  currentStep = 1;

  itemForm = this.fb.group({
    shareMethod: [''],
    url: [''],
    body: ['']
  });

  readonly totalSteps = 3;

  get progressPercent(): number {
    return Math.round((this.currentStep / this.totalSteps) * 100);
  }

  async ngOnInit() {
    const params = await this.storage.retrieve('dialog-params');
    this.addressId = params?.addressId;
    this.fromGettingStarted = !!params?.fromGettingStarted;
    this.metadataKey = `onboarding_step.${this.addressId}.${this.stepId}`;
    this.rightSidePanelService.setDialogPageTitle('Share Your Guidebook');
    this.loaded = true;
    this.cdr.markForCheck();
  }

  ngOnDestroy() {
    this.rightSidePanelService.customBackAction$.next(null);
  }

  private updateBackAction() {
    if (this.currentStep > 1) {
      this.rightSidePanelService.customBackAction$.next(() => {
        this.currentStep = this.currentStep - 1;
        this.cdr.markForCheck();
        this.updateBackAction();
      });
    } else {
      this.rightSidePanelService.customBackAction$.next(null);
    }
  }

  selectShareMethod = (method: 'url' | 'upload') => {
    this.itemForm.controls.shareMethod.setValue(method);
    this.currentStep = 2;
    this.updateBackAction();
    this.cdr.markForCheck();
  };

  goToNextStep = async () => {
    const shareMethod = this.itemForm.value.shareMethod;
    if (shareMethod === 'url' && !this.itemForm.value.url?.trim()) {
      this.util.showWarning('Please enter a URL.', 5000);
      return;
    }
    if (shareMethod === 'upload' && !this.attachments.length) {
      this.util.showWarning('Please upload at least one file.', 5000);
      return;
    }
    this.currentStep = this.currentStep + 1;
    this.updateBackAction();
    this.cdr.markForCheck();
  };

  finish = async () => {
    await this.saveConciergeItem();
  };

  addAttachment = async () => {
    try {
      const attachment = await this.conciergeApi.addAttachment();
      this.attachments.push(attachment);
      this.cdr.markForCheck();
    } catch (err) {
      this.util.showError((err.error && err.error.message) ? err.error.message : err.message, 10000);
    }
  };

  removeAttachment = (index: number) => {
    this.attachments.splice(index, 1);
    this.cdr.markForCheck();
  };

  private async saveConciergeItem() {
    const subject = 'Please set up my guidebook';
    const url = this.itemForm.value.url?.trim() || '';
    const notes = this.itemForm.value.body || '';
    const bodyParts: string[] = [];
    if (url) {
      bodyParts.push(`Guidebook URL: ${url}`);
    }
    if (notes) {
      bodyParts.push(notes);
    }

    const data = {
      body: bodyParts.join('\n\n'),
      subject,
      attachments: this.attachments.map(item => item.fileKey),
      emails: [] as string[]
    };
    const item = {
      data,
      type: 'support.concierge_task',
    };
    try {
      await Promise.all([
        lastValueFrom(this.conciergeApi.createConciergeItem(item)),
        this.markStepCompleted()
      ]);
      this.navigateBack();
    } catch (err) {
      this.util.showError((err.error && err.error.message) ? err.error.message : err.message, 10000);
    }
  }

  private async markStepCompleted() {
    await this.client.addMetadata({
      key: this.metadataKey,
      value: 'completed'
    });
  }

  private navigateBack() {
    if (this.windowService.isDesktopRes) {
      this.rightSidePanelService.closeRightSidePanel();
    } else {
      this.navCtrl.navigateForward('getting-started');
    }
  }
}
